/**
 * My applications. The way back in.
 *
 * Each card carries the one thing the applicant came to find out — how many
 * days are left, or that the period has been overrun — rather than a status
 * word that means nothing outside an office.
 */

import { useEffect, useState } from "react";
import { bi } from "@ksidc/ontology";
import { useLang, useSession } from "@ksidc/client";
import type { ApplicationListResponse, FileView } from "../api-types.js";
import { days, formatDate } from "../format.js";
import { APPLY, DISCOVER, statusHref } from "../router.js";
import { hasDraft } from "../draft.js";
import { Badge, Callout, EmptyState, ErrorNote, Loading } from "../components/ui.js";

const BAND_TONE: Record<string, "plain" | "warn" | "alarm" | "calm"> = {
  BREACHED: "alarm",
  DUE_SOON: "warn",
  PAUSED: "warn",
  RUNNING: "plain",
  DECIDED: "calm",
};

export function Applications(): JSX.Element {
  const { api } = useSession();
  const { t } = useLang();
  const [files, setFiles] = useState<FileView[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const draftWaiting = hasDraft();

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const res = await api.get<ApplicationListResponse>("/applications?mine=1");
        if (live) setFiles(res.files);
      } catch (e) {
        if (live) setError(e);
      }
    })();
    return () => {
      live = false;
    };
  }, [api]);

  return (
    <div className="stack">
      <div>
        <h1>{t(bi("My applications", "എന്റെ അപേക്ഷകൾ"))}</h1>
        <p className="lede">
          {t(
            bi(
              "Open one to see its dates, which offices have it now, and whether the Board needs anything from you.",
              "ഏതെങ്കിലും ഒന്ന് തുറന്നാൽ അതിന് ബാധകമായ തീയതികളും, ആരുടെ പക്കലാണെന്നും, നിങ്ങളിൽ നിന്ന് എന്തെങ്കിലും വേണോ എന്നും കാണാം.",
            ),
          )}
        </p>
      </div>

      {draftWaiting ? (
        <Callout
          tone="warn"
          title={bi("You have an unfinished form", "പൂർത്തിയാകാത്ത ഒരു ഫോറം ഉണ്ട്")}
        >
          <p>
            {t(
              bi(
                "A draft is saved on this device. Nothing has been sent to the Board yet.",
                "ഈ ഉപകരണത്തിൽ ഒരു കരട് സൂക്ഷിച്ചിട്ടുണ്ട്. ബോർഡിന് ഇതുവരെ ഒന്നും അയച്ചിട്ടില്ല.",
              ),
            )}
          </p>
          <a className="btn no-print" href={APPLY}>
            {t(bi("Carry on with it", "അത് തുടരുക"))}
          </a>
        </Callout>
      ) : null}

      {error ? <ErrorNote error={error} /> : null}

      {files === null ? (
        <Loading what={bi("Reading your applications", "നിങ്ങളുടെ അപേക്ഷകൾ വായിക്കുന്നു")} />
      ) : files.length === 0 ? (
        <>
          <EmptyState
            title={bi("You have not filed anything yet", "നിങ്ങൾ ഇതുവരെ ഒന്നും നൽകിയിട്ടില്ല")}
            body={bi(
              "Start by finding out which clearances your business needs. It takes four questions.",
              "നിങ്ങളുടെ യൂണിറ്റിന് ഏതൊക്കെ അനുമതികൾ വേണമെന്ന് കണ്ടെത്തിക്കൊണ്ട് തുടങ്ങുക. നാല് ചോദ്യങ്ങൾ മാത്രം.",
            )}
          />
          <div className="btn-row no-print">
            <a className="btn" href={DISCOVER}>
              {t(bi("What do I need?", "എനിക്ക് എന്തൊക്കെ വേണം?"))}
            </a>
            <a className="btn btn-secondary" href={APPLY}>
              {t(bi("Go straight to the form", "നേരിട്ട് ഫോറത്തിലേക്ക് പോകുക"))}
            </a>
          </div>
        </>
      ) : (
        <div className="stack-tight">
          {files.map((f) => (
            <a className="card-link" key={f.applicationId} href={statusHref(f.applicationId)}>
              <div className="reference">{f.referenceNumber}</div>
              <div style={{ margin: "0.4rem 0" }}>
                <Badge text={f.attention.label} tone={BAND_TONE[f.attention.band] ?? "plain"} />
              </div>
              <div className="item-sub">
                {t(
                  bi(
                    `Filed on ${formatDate(f.submittedAt, "en")}.`,
                    `${formatDate(f.submittedAt, "ml")}-ന് നൽകി.`,
                  ),
                )}
              </div>
              {f.clock.deemedDate ? (
                <div className="item-sub">
                  {t(
                    bi(
                      `If the Board has not decided by ${formatDate(f.clock.deemedDate, "en")}, your clearance counts as given.`,
                      `${formatDate(f.clock.deemedDate, "ml")}-നകം ബോർഡ് തീരുമാനം എടുത്തില്ലെങ്കിൽ, നിങ്ങളുടെ അനുമതി നൽകിയതായി കണക്കാക്കും.`,
                    ),
                  )}
                </div>
              ) : null}
              {f.openQueries.length > 0 ? (
                <div className="item-sub">
                  <strong>
                    {t(
                      bi(
                        `${f.openQueries.length === 1 ? "One question is" : f.openQueries.length + " questions are"} waiting for your answer. The days are not being counted until you reply.`,
                        `${f.openQueries.length} ചോദ്യം നിങ്ങളുടെ മറുപടിക്കായി കാത്തിരിക്കുന്നു. നിങ്ങൾ മറുപടി നൽകുന്നതുവരെ ദിവസങ്ങൾ എണ്ണുന്നില്ല.`,
                      ),
                    )}
                  </strong>
                </div>
              ) : f.clock.started && !f.decided ? (
                <div className="item-sub">
                  {t(
                    bi(
                      `${days(f.clock.daysRemaining).en} left of the Board\u2019s time limit.`,
                      `ബോർഡിന്റെ സമയപരിധിയിൽ ${days(f.clock.daysRemaining).ml} ബാക്കി.`,
                    ),
                  )}
                </div>
              ) : null}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
